import type { ReactNode } from 'react';
import { Button, cn, Input, Select } from '@/core/ui';
import { ArrowDown, ArrowUp, Plus, Trash } from '@/core/ui/icons';
import {
  branchesOf,
  isValueless,
  itemsOf,
  newBranch,
  newChoiceItem,
  newRule,
  OPERATORS,
  rulesOf,
} from '../../domain/blocks';
import type { BlockOf, Branch, ChoiceItem, Rule, RuleOp } from '../../domain/types';
import { Section, VariableSelect } from './fields';

const MAX_ITEMS = 20;
const MAX_BRANCHES = 10;
const MAX_RULES = 10;

function move<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1.5 text-fg-subtle hover:bg-surface hover:text-fg disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** As opções de um bloco "Botões": texto, valor guardado (opcional) e ordem. */
export function ChoiceItemsEditor({
  block,
  onChange,
}: {
  block: BlockOf<'input_choice'>;
  onChange: (b: BlockOf<'input_choice'>) => void;
}) {
  const items = itemsOf(block);
  const set = (next: ChoiceItem[]) => onChange({ ...block, items: next });
  const patch = (i: number, p: Partial<ChoiceItem>) =>
    set(items.map((it, j) => (j === i ? { ...it, ...p } : it)));

  return (
    <Section title="Opções">
      <ol className="space-y-2">
        {items.map((item, i) => (
          <li key={item.id} className="space-y-1.5 rounded-xl bg-surface p-2.5">
            <div className="flex items-center gap-1">
              <Input
                aria-label={`Texto da opção ${i + 1}`}
                value={item.label}
                maxLength={300}
                placeholder={`Opção ${i + 1}`}
                onChange={(e) => patch(i, { label: e.target.value })}
                className="h-9"
              />
              <IconButton label="Subir opção" disabled={i === 0} onClick={() => set(move(items, i, -1))}>
                <ArrowUp aria-hidden className="size-3.5" />
              </IconButton>
              <IconButton
                label="Descer opção"
                disabled={i === items.length - 1}
                onClick={() => set(move(items, i, 1))}
              >
                <ArrowDown aria-hidden className="size-3.5" />
              </IconButton>
              <IconButton label="Apagar opção" onClick={() => set(items.filter((_, j) => j !== i))}>
                <Trash aria-hidden className="size-3.5" />
              </IconButton>
            </div>
            <Input
              aria-label={`Valor guardado da opção ${i + 1}`}
              value={item.value}
              maxLength={300}
              placeholder="Valor guardado (opcional — vazio guarda o texto)"
              onChange={(e) => patch(i, { value: e.target.value })}
              className="h-8 text-xs"
            />
          </li>
        ))}
      </ol>
      <Button
        size="sm"
        variant="secondary"
        disabled={items.length >= MAX_ITEMS}
        onClick={() =>
          set([...items, newChoiceItem(new Set(items.map((x) => x.id)), `Opção ${items.length + 1}`)])
        }
      >
        <Plus aria-hidden className="size-3.5" />
        Opção
      </Button>
      <p className="text-xs text-fg-subtle">
        {block.multiple
          ? 'Escolha múltipla não ramifica: a conversa segue pela saída do card.'
          : 'Cada opção pode levar a um card diferente; opção sem ligação segue para o próximo bloco.'}
      </p>
    </Section>
  );
}

/** Condições: a primeira verdadeira vence; nenhuma → "Senão" (ou o próximo bloco). */
export function ConditionEditor({
  block,
  onChange,
}: {
  block: BlockOf<'condition'>;
  onChange: (b: BlockOf<'condition'>) => void;
}) {
  const branches = branchesOf(block);
  const set = (next: Branch[]) => onChange({ ...block, branches: next });
  const patch = (i: number, p: Partial<Branch>) =>
    set(branches.map((b, j) => (j === i ? { ...b, ...p } : b)));
  const patchRule = (i: number, r: number, p: Partial<Rule>) =>
    patch(i, { rules: rulesOf(branches[i]!).map((x, k) => (k === r ? { ...x, ...p } : x)) });

  return (
    <div className="space-y-3">
      {branches.map((branch, i) => {
        const rules = rulesOf(branch);
        return (
          <Section key={branch.id} title={`Condição ${i + 1}`}>
            <div className="flex items-center gap-1">
              <Input
                aria-label={`Nome da condição ${i + 1}`}
                value={branch.label}
                maxLength={300}
                placeholder="Nome no card (opcional)"
                onChange={(e) => patch(i, { label: e.target.value })}
                className="h-9"
              />
              <IconButton
                label="Subir condição"
                disabled={i === 0}
                onClick={() => set(move(branches, i, -1))}
              >
                <ArrowUp aria-hidden className="size-3.5" />
              </IconButton>
              <IconButton
                label="Descer condição"
                disabled={i === branches.length - 1}
                onClick={() => set(move(branches, i, 1))}
              >
                <ArrowDown aria-hidden className="size-3.5" />
              </IconButton>
              <IconButton label="Apagar condição" onClick={() => set(branches.filter((_, j) => j !== i))}>
                <Trash aria-hidden className="size-3.5" />
              </IconButton>
            </div>

            {rules.length > 1 && (
              <Select
                aria-label={`Como combinar as regras da condição ${i + 1}`}
                value={branch.match}
                onChange={(e) => patch(i, { match: e.target.value as Branch['match'] })}
                className="h-9"
              >
                <option value="all">Todas as regras valem (e)</option>
                <option value="any">Qualquer regra vale (ou)</option>
              </Select>
            )}

            <ol className="space-y-2">
              {rules.map((rule, r) => (
                <li key={r} className={cn('space-y-1.5 rounded-xl bg-surface p-2.5')}>
                  <div className="flex items-end gap-1">
                    <div className="min-w-0 flex-1">
                      <VariableSelect
                        label={r === 0 ? 'Se' : branch.match === 'any' ? 'ou' : 'e'}
                        value={rule.variable}
                        readable
                        onChange={(variable) => patchRule(i, r, { variable })}
                      />
                    </div>
                    <IconButton
                      label="Apagar regra"
                      onClick={() => patch(i, { rules: rules.filter((_, k) => k !== r) })}
                    >
                      <Trash aria-hidden className="size-3.5" />
                    </IconButton>
                  </div>
                  <Select
                    aria-label="Operador"
                    value={rule.op}
                    onChange={(e) => patchRule(i, r, { op: e.target.value as RuleOp })}
                    className="h-9"
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                  {!isValueless(rule.op) && (
                    <Input
                      aria-label="Valor comparado"
                      value={rule.value}
                      maxLength={300}
                      placeholder="Valor (aceita {{variável}})"
                      onChange={(e) => patchRule(i, r, { value: e.target.value })}
                      className="h-9"
                    />
                  )}
                </li>
              ))}
            </ol>
            <Button
              size="sm"
              variant="ghost"
              disabled={rules.length >= MAX_RULES}
              onClick={() => patch(i, { rules: [...rules, newRule()] })}
            >
              <Plus aria-hidden className="size-3.5" />
              Regra
            </Button>
          </Section>
        );
      })}
      <Button
        size="sm"
        variant="secondary"
        disabled={branches.length >= MAX_BRANCHES}
        onClick={() => set([...branches, newBranch(new Set(branches.map((b) => b.id)))])}
      >
        <Plus aria-hidden className="size-3.5" />
        Condição
      </Button>
      <p className="text-xs text-fg-subtle">
        A primeira condição verdadeira decide a saída. Se nenhuma valer, segue pela saída “Senão” (no último
        bloco do card) ou para o próximo bloco.
      </p>
    </div>
  );
}
